import yaml from 'js-yaml';

// This util reads the environment from Vite (import.meta.env) and parses the YAML feature config.
// It works both in dev and after bundling (Vite injects import.meta.env at build time).

// Type for a single feature
export interface FeatureFlag {
  name: string;
  environments?: string[] | null;
  plans?: string[] | null; // Plans that can access this feature (e.g., ['business', 'enterprise'])
}

// Type for the YAML config
interface FeaturesYamlConfig {
  environments: string[];
  features: FeatureFlag[];
}

// Helper to get the current environment from Vite
export function getCurrentEnvironment(): string {
  // Vite exposes the mode as import.meta.env.MODE
  // You may want to map this to your YAML envs if needed
  // console.log('ENVIRONMENT: ', import.meta.env.MODE);
  return import.meta.env.MODE || 'development';
}

// Helper to load and parse the YAML config
// NOTE: This will be bundled as a static asset, so we use an import assertion
import featuresYaml from '@/config/features.yaml?raw';

export function getAvailableFeatures(): FeatureFlag[] {
  let config: FeaturesYamlConfig;
  try {
    config = yaml.load(featuresYaml) as FeaturesYamlConfig;
  } catch (e) {
    console.error('Failed to parse features.yaml:', e);
    return [];
  }
  const env = getCurrentEnvironment();
  // Only include features that explicitly list the current environment.
  // If a feature has no environments configured (null/undefined), treat it as unavailable.
  const features = (config.features || []).filter(
    (f) => Array.isArray(f.environments) && f.environments.includes(env)
  );
  // console.log('FEATURES: ', features);
  return features;
}

// Optionally, export all features and environments as well
export function getAllFeatures(): FeatureFlag[] {
  let config: FeaturesYamlConfig;
  try {
    config = yaml.load(featuresYaml) as FeaturesYamlConfig;
  } catch (e) {
    return [];
  }
  return config.features || [];
}

export function getAllEnvironments(): string[] {
  let config: FeaturesYamlConfig;
  try {
    config = yaml.load(featuresYaml) as FeaturesYamlConfig;
  } catch (e) {
    return [];
  }
  return config.environments || [];
}

// Helper to check if a specific feature is enabled for the current environment
export function isFeatureEnabled(featureName: string): boolean {
  const features = getAvailableFeatures();
  return features.some(f => f.name === featureName);
}

// Normalize plan name to lowercase for comparison
function normalizePlanName(plan: string | undefined | null): string {
  if (!plan) return '';
  const lower = plan.toLowerCase();
  // Map various plan names to canonical forms
  if (lower.includes('starter')) return 'starter';
  if (lower.includes('business') || lower === 'iso') return 'business';
  if (lower.includes('enterprise')) return 'enterprise';
  return lower;
}

/**
 * Check if a feature is enabled for the current environment AND the given tenancy plan.
 * Features can specify which plans they're available for via the `plans` property.
 * If no plans are specified, the feature is available to all plans (environment check only).
 * 
 * @param featureName - The name of the feature to check
 * @param tenancyPlan - The current tenancy's plan (e.g., 'Starter Plan', 'Business Plan', 'Enterprise Plan')
 * @returns boolean - Whether the feature is enabled for this environment and plan
 */
export function isFeatureEnabledForPlan(featureName: string, tenancyPlan: string | undefined | null): boolean {
  // First check if feature is enabled for current environment
  const features = getAvailableFeatures();
  const feature = features.find(f => f.name === featureName);
  
  if (!feature) return false;
  
  // If no plans are specified, feature is available to all plans
  if (!feature.plans || feature.plans.length === 0) return true;
  
  // Check if the tenancy plan is in the allowed plans list
  const normalizedPlan = normalizePlanName(tenancyPlan);
  return feature.plans.some(p => normalizePlanName(p) === normalizedPlan);
}

